import { useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLaptopCode, faRobot, faRocket, faMagnifyingGlass, faCode } from "@fortawesome/free-solid-svg-icons";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SERVICES = [
  {
    icon: faLaptopCode,
    title: "Full-Stack MVP & High-Performance Backends",
    description:
      "Partner with me to design, build, and ship production-ready web and mobile applications in weeks. From high-performance FastAPI and Node.js backends to multi-tenant environments and secure payments.",
    highlights: ["Next.js / React", "FastAPI & Node.js", "Stripe Payments", "Multi-Tenant"],
  },
  {
    icon: faRobot,
    title: "Custom AI Agents & Enterprise RAG",
    description:
      "Intelligent AI systems integrated directly into your platforms — custom agents that automate workflows, enterprise RAG powered by Vector Databases, and LLM pipelines that scale your operations.",
    highlights: ["AI Agents", "Enterprise RAG", "Vector DBs", "Workflow Automation"],
  },
  {
    icon: faRocket,
    title: "Legacy Modernization & Cloud Infrastructure",
    description:
      "Modernize aging systems and deploy robust cloud infrastructure without disruption. From database query optimization to full-stack architecture upgrades that transform legacy products into performant platforms.",
    highlights: ["Cloud Architecture", "Query Optimization", "Performance", "Scaling"],
  },
];

const WORKFLOW = [
  {
    step: "01",
    icon: faMagnifyingGlass,
    title: "Technical Discovery",
    description:
      "A focused session to understand your goals, technical requirements, and timeline. Clear scope document and architecture recommendation within 48 hours.",
  },
  {
    step: "02",
    icon: faCode,
    title: "Agile Execution & Async Demos",
    description:
      "Tight sprint cycles with regular async demos and progress updates. Full visibility through shared repos, staging environments, and weekly check-ins.",
  },
  {
    step: "03",
    icon: faRocket,
    title: "Deployment & Scalable Support",
    description:
      "Production deployment, performance validation, and documentation handoff. Ongoing support and iteration post-launch so your product keeps improving.",
  },
];

const Services = () => {
  const gridRef = useRef(null);
  const headRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headRef.current,
        { y: 36, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.75, ease: "power3.out",
          scrollTrigger: { trigger: headRef.current, start: "top 88%", once: true },
        }
      );
      const cards = gridRef.current?.querySelectorAll(".service-card");
      if (cards?.length) {
        gsap.fromTo(
          cards,
          { y: 48, opacity: 0, scale: 0.95 },
          {
            y: 0, opacity: 1, scale: 1, duration: 0.6, stagger: 0.1, ease: "power3.out",
            scrollTrigger: { trigger: gridRef.current, start: "top 85%", once: true },
          }
        );
      }
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="services"
      className="relative py-20 md:py-28 lg:py-32 px-4 sm:px-5 md:px-10 lg:px-12 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto relative z-10">

        {/* ── Section header ── */}
        <div ref={headRef} className="text-center mb-12 sm:mb-16 opacity-0">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background border border-border
                          text-xs font-semibold mb-6 tracking-widest uppercase text-muted-foreground">
            Services & Workflow
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4 font-heading tracking-tight">
            Strategic Solutions for Founders &amp; Agencies
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto leading-relaxed">
            End-to-end engineering partnership designed to accelerate your product roadmap, automate
            operations, and scale infrastructure — with the reliability of a senior technical partner.
          </p>
        </div>

        {/* ── Bento service cards ── */}
        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          {SERVICES.map((service) => (
            <div
              key={service.title}
              className="service-card opacity-0 group relative rounded-2xl sm:rounded-3xl
                         glass-card p-6 sm:p-8 flex flex-col cursor-default overflow-hidden
                         transition-all duration-300 hover:-translate-y-1.5 hover:shadow-glow min-w-0"
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/15
                              flex items-center justify-center text-primary text-xl mb-5
                              group-hover:bg-primary group-hover:text-primary-foreground
                              group-hover:border-primary transition-all duration-300 flex-shrink-0">
                <FontAwesomeIcon icon={service.icon} />
              </div>

              <h3 className="text-lg sm:text-xl font-bold text-foreground font-heading mb-3
                             tracking-tight group-hover:text-primary transition-colors leading-snug">
                {service.title}
              </h3>

              <p className="text-muted-foreground text-sm leading-relaxed flex-grow mb-6">
                {service.description}
              </p>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-1.5 pt-5 border-t border-border/50">
                {service.highlights.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] font-mono font-bold tracking-wider uppercase
                               px-2.5 py-1 rounded-lg bg-secondary/80 text-muted-foreground
                               group-hover:text-primary group-hover:bg-primary/10
                               border border-border/60 group-hover:border-primary/20
                               transition-colors duration-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Workflow strip ── */}
        <div className="mt-16 sm:mt-20">
          <div className="text-center mb-8 sm:mb-10">
            <h3 className="text-xl md:text-2xl font-bold text-foreground font-heading tracking-tight mb-2">
              How We Work Together
            </h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed">
              Structured, transparent process — results fast with zero surprises.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {WORKFLOW.map((item) => (
              <div
                key={item.step}
                className="group relative rounded-2xl glass-card p-6 sm:p-7 flex flex-col
                           cursor-default transition-all duration-300 hover:-translate-y-1 min-w-0"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/15
                                  flex items-center justify-center text-primary
                                  group-hover:bg-primary group-hover:text-primary-foreground
                                  group-hover:border-primary transition-all duration-300 flex-shrink-0">
                    <FontAwesomeIcon icon={item.icon} className="text-sm" />
                  </div>
                  <span className="text-[10px] font-mono font-bold tracking-widest uppercase text-muted-foreground">
                    Step {item.step}
                  </span>
                </div>
                <h4 className="text-base font-bold text-foreground font-heading mb-2
                               tracking-tight group-hover:text-primary transition-colors">
                  {item.title}
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Services;
